<template>
  <div class="rules-wrapper container py-5">
    <section class="text-center mb-5">
      <h1 class="display-5 fw-bold text-gold mb-3">Regras de Vampiro: A Máscara</h1>
      <p class="lead text-light-75">
        Explore o Mundo das Trevas com uma visão completa das regras, da história e das mecânicas
        que fazem de <em>Vampiro: A Máscara</em> um dos RPGs mais icônicos já criados.
      </p>
    </section>

    <section class="mb-5">
      <div class="row g-4 align-items-center flex-column-reverse flex-lg-row">
        <div class="col-lg-6">
          <h2 class="section-title">Uma breve jornada pelo cenário</h2>
          <p>
            Criado pela White Wolf em 1991, <strong>Vampiro: A Máscara</strong> apresenta um mundo moderno
            onde criaturas da noite conspiram nas sombras. Os jogadores assumem o papel de vampiros &mdash;
            também chamados de Cainitas &mdash; divididos em clãs com poderes sobrenaturais e agendas políticas
            conflitantes.
          </p>
          <p>
            A ambientação combina terror pessoal com intriga. A humanidade do personagem é constantemente
            testada pelo chamado da Besta interior, enquanto se equilibra a Máscara, o pacto silencioso que
            mantém a existência vampírica em segredo dos mortais.
          </p>
          <ul>
            <li><strong>Época:</strong> Contemporânea, com influência gótica-punk.</li>
            <li><strong>Tema central:</strong> Perda de humanidade, política sombria e horror pessoal.</li>
            <li><strong>Tom:</strong> Melancólico, dramático e cheio de dilemas morais.</li>
          </ul>
        </div>
        <div class="col-lg-6 text-center">
          <figure class="image-frame">
            <img
              :src="ruleImage01"
              alt="Silhueta de vampiro sob a lua"
              class="img-fluid rounded shadow"
              loading="lazy"
            >
            <figcaption class="mt-3 small text-light-75">
              A eterna dança entre poder e humanidade define cada crônica de Vampiro: A Máscara.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section class="mb-5">
      <div class="card bg-dark border-gold shadow-lg">
        <div class="card-body p-4 p-lg-5">
          <h2 class="section-title text-center mb-4">Estrutura de uma Crônica</h2>
          <div class="row g-4">
            <div class="col-md-4">
              <h3 class="h5 text-gold">Crônica</h3>
              <p>
                Campanha abrangente conduzida pelo Narrador. Define o tom, os antagonistas principais e o arco
                dramático de longo prazo.
              </p>
            </div>
            <div class="col-md-4">
              <h3 class="h5 text-gold">Sessão</h3>
              <p>
                Reunião individual onde eventos específicos acontecem. Geralmente inclui missões, intrigas e
                consequências diretas para os personagens.
              </p>
            </div>
            <div class="col-md-4">
              <h3 class="h5 text-gold">Cena</h3>
              <p>
                Unidade narrativa menor, focada em um local ou objetivo. É nela que os testes de habilidades e as
                interações intensas ocorrem.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="mb-5">
      <h2 class="section-title">Construindo a Ficha de Personagem</h2>
      <div class="row g-4">
        <div class="col-lg-6">
          <p>
            A ficha apresenta os elementos que definem o personagem: <strong>Atributos</strong>,
            <strong>Habilidades</strong>, <strong>Disciplinas</strong>, <strong>Vantagens</strong> e a medida
            de <strong>Humanidade</strong>. A criação tradicional segue cinco passos:
          </p>
          <ol>
            <li>Definir conceito, clã, geração e natureza/comportamento.</li>
            <li>Distribuir pontos em Atributos (Físicos, Sociais, Mentais).</li>
            <li>Distribuir pontos em Habilidades (Talentos, Perícias, Conhecimentos).</li>
            <li>Escolher Disciplinas, Virtudes, Antecedentes e Vantagens únicas do clã.</li>
            <li>Personalizar com Históricos, Convicções e Toques finais (Força de Vontade, Vitalidade, etc.).</li>
          </ol>
        </div>
        <div class="col-lg-6">
          <div class="table-responsive">
            <table class="table table-dark table-striped align-middle mb-0">
              <thead>
                <tr>
                  <th>Elemento</th>
                  <th>Descrição</th>
                  <th>Uso em jogo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Atributos</td>
                  <td>Força, Destreza, Vigor, Carisma, Manipulação, Aparência, Percepção, Inteligência, Raciocínio.</td>
                  <td>Base para testes e rolagens de dano.</td>
                </tr>
                <tr>
                  <td>Habilidades</td>
                  <td>Talentos inatos, perícias treinadas e conhecimentos acadêmicos.</td>
                  <td>Complementam atributos para rolagens de dados.</td>
                </tr>
                <tr>
                  <td>Disciplinas</td>
                  <td>Poderes sobrenaturais exclusivos de cada clã.</td>
                  <td>Concedem efeitos especiais mediante gasto de sangue ou testes.</td>
                </tr>
                <tr>
                  <td>Virtudes</td>
                  <td>Consciência/Convicção, Autocontrole/Instinto, Coragem.</td>
                  <td>Protegem a Humanidade e resistem a Frenesis.</td>
                </tr>
                <tr>
                  <td>Recursos Vitais</td>
                  <td>Sangue (Reserva de Sangue) e Força de Vontade.</td>
                  <td>Ativam disciplinas, curam dano e sustentam o personagem.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

    <section class="mb-5">
      <div class="row g-4 align-items-rigth">
        <div class="col-lg-6 text-rigth">
          <figure class="image-frame">
            <img
              :src="ruleImage03"
              alt="Mãos segurando dados de RPG"
              class="img-fluid rounded shadow img-600"
              loading="lazy"
            >
            <figcaption class="mt-3 small text-light-75">
              A tensão de cada rolagem define o destino do cainita.
            </figcaption>
          </figure>
        </div>
        <div class="col-lg-6">
          <h2 class="section-title">Mecânicas Fundamentais</h2>
          <p>
            O sistema utiliza dados de dez lados (d10) em um esquema de <strong>Storyteller</strong>. Para realizar
            uma ação, some o atributo relevante à habilidade adequada e role um número de dados igual ao total
            resultante.
          </p>
          <ul>
            <li><strong>Dificuldade:</strong> Número alvo entre 3 e 9 definido pelo Narrador.</li>
            <li><strong>Sucessos:</strong> Cada dado igual ou superior à dificuldade conta como um sucesso.</li>
            <li><strong>Falhas Críticas:</strong> Se nenhum sucesso for obtido e pelo menos um 1 for rolado, ocorre
              um desastre.</li>
            <li><strong>Sucessos Extras:</strong> Dois ou mais sucessos podem gerar efeitos aprimorados ou reduzir o
              tempo de execução.</li>
          </ul>
          <p>
            Para ações opostas, compare sucessos; quem obtiver mais supera o adversário. Ações estendidas exigem
            múltiplas rolagens acumulando sucessos, enquanto ações em equipe permitem que personagens auxiliem com
            sucessos adicionais.
          </p>
        </div>
      </div>
    </section>

    <section class="mb-5">
      <h2 class="section-title">Combate e Batalhas por Turno</h2>
      <p>
        Confrontos em <em>Vampiro: A Máscara</em> são intensos e perigosos. Cada turno segue etapas claras:
      </p>
      <ol>
        <li><strong>Iniciativa:</strong> Role Destreza + Prontidão. O maior resultado age primeiro.</li>
        <li><strong>Declarações:</strong> Jogadores anunciam suas ações, do mais lento para o mais rápido.</li>
        <li><strong>Rolagens de Ataque:</strong> Atributo + Habilidade apropriada (por exemplo, Destreza + Armas de Fogo).</li>
        <li><strong>Rolagens de Dano:</strong> Variam conforme o tipo de ataque. Vampiros podem absorver dano contuso e letal
          com rolagens de Vigor; dano agravado normalmente só pode ser absorvido com Fortitude.</li>
        <li><strong>Resolução:</strong> Os efeitos são aplicados, contabilizando perda de Vitalidade, gastos de sangue e
          possíveis Frenesis.</li>
      </ol>
      <div class="row g-4 mt-4">
        <div class="col-md-6">
          <div class="card bg-dark border-gold h-100">
            <div class="card-body">
              <h3 class="h5 text-gold">Exemplo: Combate Corpo a Corpo</h3>
              <p>
                Helena (Brujah) tenta desarmar um guarda. Ela rola Força + Briga (6 dados) contra dificuldade 6 e obtém
                3 sucessos. O guarda rola Destreza + Esquiva (4 dados) e consegue apenas 1 sucesso. Helena acerta com 2
                sucessos de margem, adicionando-os ao dano de Força (3 dados), totalizando 5 dados de dano. Após a rolagem,
                o Narrador aplica o dano e verifica se o guarda resiste com Vigor.
              </p>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card bg-dark border-gold h-100">
            <div class="card-body">
              <h3 class="h5 text-gold">Exemplo: Ataque à Distância</h3>
              <p>
                Marcus (Toreador) dispara uma pistola. Ele rola Destreza + Armas de Fogo (5 dados) contra dificuldade 7,
                obtendo 4 sucessos. Como o alvo não tinha cobertura, o dano base da arma (4 dados) recebe 3 sucessos extras
                de margem, totalizando 7 dados de dano. O alvo só pode absorver dano contuso, tornando o impacto devastador.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="mb-5">
      <h2 class="section-title">Testes de Disciplinas e Poderes</h2>
      <p>
        Disciplinas representam as habilidades sobrenaturais dos vampiros. Ativar um poder geralmente exige gasto de
        sangue e uma rolagem específica. Alguns exemplos:
      </p>
      <div class="row g-4">
        <div class="col-lg-4">
          <div class="card bg-dark border-gold h-100">
            <div class="card-body">
              <h3 class="h5 text-gold">Auspex</h3>
              <p><strong>Teste:</strong> Percepção + Consciência (dificuldade 6).</p>
              <p>
                Permite detectar presenças ocultas, ler auras e ampliar sentidos. Sucessos adicionais fornecem detalhes
                mais precisos sobre emoções e intenções.
              </p>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="card bg-dark border-gold h-100">
            <div class="card-body">
              <h3 class="h5 text-gold">Dominação</h3>
              <p><strong>Teste:</strong> Manipulação + Liderança (dificuldade varia conforme a força de vontade do alvo).</p>
              <p>
                Permite impor comandos mentais. Um alvo que resista com Força de Vontade pode anular o efeito, exigindo
                sucessos adicionais do usuário.
              </p>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="card bg-dark border-gold h-100">
            <div class="card-body">
              <h3 class="h5 text-gold">Fortitude</h3>
              <p><strong>Teste:</strong> Não requer rolagem para absorver dano agravado; adiciona dados de absorção.</p>
              <p>
                Representa a resistência sobrenatural. Pode ser ativada reflexivamente para suportar fogo, luz solar ou
                ataques místicos.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="mb-5">
      <h2 class="section-title">Testes de Habilidades em Ação</h2>
      <div class="row g-4">
        <div class="col-md-6">
          <div class="card bg-dark border-gold h-100">
            <div class="card-body">
              <h3 class="h5 text-gold">Infiltração Silenciosa</h3>
              <p>
                Uma Malkaviana tenta invadir um museu sem ser vista. Ela rola Destreza + Furtividade contra dificuldade 7.
                Dois sucessos garantem que ela evite os guardas, mas um 1 anula um sucesso: ela ainda passa, porém deixa um
                indício de sua presença para futuras consequências.
              </p>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card bg-dark border-gold h-100">
            <div class="card-body">
              <h3 class="h5 text-gold">Intriga Social</h3>
              <p>
                Um Ventrue planeja convencer o Príncipe a apoiá-lo. Ele rola Carisma + Etiqueta contra dificuldade 6.
                Quatro sucessos permitem que ele conquiste um favor temporário, mas o Narrador pede um teste secundário de
                Empatia para interpretar os caprichos do Príncipe.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="mb-5">
      <h2 class="section-title">Humanidade, Frenesi e a Besta</h2>
      <p>
        A Humanidade mede o quão próximo o vampiro permanece dos mortais. Baixos níveis resultam em comportamento
        monstruoso e maior risco de Frenesi. O Narrador pode solicitar testes de Autocontrole ou Instinto sempre que o
        personagem enfrentar insultos, fome intensa ou humilhações severas.
      </p>
      <ul>
        <li><strong>Degeneração:</strong> Ações cruéis podem diminuir a Humanidade após testes de Consciência.</li>
        <li><strong>Frenesi:</strong> Falhar em Autocontrole resulta em perda temporária de controle; a Besta assume.</li>
        <li><strong>Remorso:</strong> Após um Frenesi, um teste de Consciência avalia se o personagem sente culpa.</li>
      </ul>
      <p>
        Manter conexões mortais, seguir convicções pessoais e interpretar conflitos internos são elementos chave para
        crônicas emocionantes.
      </p>
    </section>

    <section class="mb-5">
      <h2 class="section-title">Sugestões para Narradores</h2>
      <div class="card bg-dark border-gold">
        <div class="card-body">
          <ul class="mb-0">
            <li>Intercale cenas de horror com momentos de humanidade para criar contraste emocional.</li>
            <li>Use antagonistas memoráveis: Príncipes, anciões, Lupinos ou caçadores da Inquisição.</li>
            <li>Recompense escolhas dramáticas com Pontos de Experiência e oportunidades de desenvolvimento.</li>
            <li>Personalize a cidade da crônica com locais icônicos, refúgios e boatos sobrenaturais.</li>
            <li>Mantenha o ritmo fluido, alternando intriga política com ações rápidas e tensas.</li>
          </ul>
        </div>
      </div>
    </section>

    <section class="text-center">
      <h2 class="section-title">Entre na Noite</h2>
      <p>
        Reúna seu coterie, prepare a ficha e permita que a narrativa explore os limites entre o humano e o monstro. Em
        <em>Vampiro: A Máscara</em>, cada decisão ecoa pelas ruas sombrias do Mundo das Trevas.
      </p>
    </section>
  </div>
</template>

<script>
import ruleImage01 from '../assets/rulesImages/ruleImage_02.png'
import ruleImage03 from '../assets/rulesImages/ruleImage_03.png'

export default {
  name: 'RulesView',
  data() {
    return {
      ruleImage01: ruleImage01,
      ruleImage03: ruleImage03
    }
  }

}
</script>

<style scoped>
.rules-wrapper {
  color: #f8f9fa;
}

.section-title {
  color: var(--vampire-gold);
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.text-gold {
  color: var(--vampire-gold);
}

.text-light-75 {
  color: rgba(248, 249, 250, 0.75);
}

.border-gold {
  border: 1px solid rgba(218, 165, 32, 0.35);
}

.image-frame img {
  border: 1px solid rgba(218, 165, 32, 0.25);
}

.img-600 {
  height: 400px;
  width: 636px; 
}

.image-frame figcaption {
  color: rgba(248, 249, 250, 0.7);
}

.table thead th {
  border-bottom: 2px solid rgba(218, 165, 32, 0.5);
}

.table td,
.table th {
  border-color: rgba(248, 249, 250, 0.1);
}

.card {
  background: rgba(17, 17, 17, 0.9);
}
</style>
